// useBranchengruppen — Branchen-Klassifikation einer Firma in der Detailansicht.
//
// Die Zuordnungen sind eine eigene Ressource (`/contact/:id/branchengruppen`),
// die Hauptbranche steht zusätzlich als `Contact.branchengruppeId` +
// `Contact.brancheFreitext` am Kontakt. Der Hook hält beides zusammen: Er lädt
// die Liste, kennt ihren „ungespeichert"-Zustand und schreibt sie — gespeichert
// wird aber über DENSELBEN Knopf wie die Stammdaten.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  branchengruppenLaden,
  branchengruppenSpeichern,
  type BranchengruppeZuordnung,
} from "../../services/branchengruppeService";
import {
  auswahlNormalisieren,
  auswahlSignatur,
  hauptbranche,
  hauptbranchePfad,
  type BrancheGruppenWahl,
} from "../forms/widgets/brancheGruppenLogik";
import { useBrancheGruppen } from "../../data/brancheGruppen";

export interface BranchengruppenState {
  auswahl: BrancheGruppenWahl[];
  setAuswahl: (next: BrancheGruppenWahl[]) => void;
  /** Es gibt ungespeicherte Änderungen (Auswahl, Reihenfolge oder Freitext). */
  dirty: boolean;
  laden: boolean;
  ladefehler: string | null;
  /** Schreibt die Liste; wirft bei Fehler, damit der Aufrufer melden kann. */
  speichern: () => Promise<void>;
  /** Anzeigepfad der Hauptbranche einer (auch noch nicht gesetzten) Auswahl. */
  pfadVon: (auswahl: BrancheGruppenWahl[]) => string;
  /** Freitext der Hauptbranche — geht als `brancheFreitext` an den Kontakt. */
  freitextVon: (auswahl: BrancheGruppenWahl[]) => string | null;
}

export function useBranchengruppen(
  contactId: number,
  aktiv: boolean,
  /** `Contact.branchengruppeId` — Hauptbranche, falls die Liste leer ist. */
  bestandsHauptbranche: number | null | undefined,
  /** `Contact.brancheFreitext` — passend dazu. */
  bestandsFreitext: string | null | undefined,
): BranchengruppenState {
  const { rows } = useBrancheGruppen();
  const [auswahl, setAuswahl] = useState<BrancheGruppenWahl[]>([]);
  const [basis, setBasis] = useState("[]");
  const [laden, setLaden] = useState(false);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const [geladen, setGeladen] = useState(false);
  // Die Übernahme der Bestands-Hauptbranche darf genau einmal je Kontakt
  // passieren — sonst käme eine gerade entfernte Branche sofort zurück.
  const uebernommen = useRef(false);

  useEffect(() => {
    uebernommen.current = false;
    setAuswahl([]);
    setBasis("[]");
    setGeladen(false);
    setLadefehler(null);
    if (!aktiv || !contactId) return;
    let lebt = true;
    setLaden(true);
    branchengruppenLaden(contactId)
      .then((r: BranchengruppeZuordnung[]) => {
        if (!lebt) return;
        const rein = auswahlNormalisieren(r);
        setAuswahl(rein);
        setBasis(auswahlSignatur(rein));
      })
      .catch((e) => {
        if (lebt) setLadefehler((e as Error).message);
      })
      .finally(() => {
        if (!lebt) return;
        setLaden(false);
        setGeladen(true);
      });
    return () => {
      lebt = false;
    };
  }, [contactId, aktiv]);

  // Bestand sichtbar machen: Trägt der Kontakt eine Hauptbranche, aber keine
  // Zuordnungszeile (etwa weil sie über die Anlage-Maske gesetzt wurde und der
  // Nachtrag scheiterte), stünde die Klassifikation sonst leer da. Die Baseline
  // wandert mit — es ist der gespeicherte Stand, keine Änderung.
  useEffect(() => {
    if (!geladen || uebernommen.current) return;
    if (auswahl.length > 0 || bestandsHauptbranche == null) return;
    uebernommen.current = true;
    const start = auswahlNormalisieren([
      { branchengruppeId: bestandsHauptbranche, freitext: bestandsFreitext ?? null },
    ]);
    setAuswahl(start);
    setBasis(auswahlSignatur(start));
  }, [geladen, auswahl.length, bestandsHauptbranche, bestandsFreitext]);

  const speichern = useCallback(async () => {
    const rein = auswahlNormalisieren(auswahl);
    await branchengruppenSpeichern(contactId, rein);
    // Bewusst der gesendete Stand statt der Server-Antwort: Die Reihenfolge ist
    // fachlich (erste Zeile = Hauptbranche), und eine abweichend sortierte
    // Antwort verschöbe die Hauptbranche still.
    setAuswahl(rein);
    setBasis(auswahlSignatur(rein));
  }, [auswahl, contactId]);

  const pfadVon = useCallback(
    (a: BrancheGruppenWahl[]) => hauptbranchePfad(rows, a),
    [rows],
  );

  const freitextVon = useCallback(
    (a: BrancheGruppenWahl[]) => (hauptbranche(a)?.freitext ?? "").trim() || null,
    [],
  );

  return {
    auswahl,
    setAuswahl,
    dirty: auswahlSignatur(auswahl) !== basis,
    laden,
    ladefehler,
    speichern,
    pfadVon,
    freitextVon,
  };
}
